// Standard library imports
import React, { useState, useEffect } from 'react';

// External library import
import { UilTrash, UilPen, UilImport, UilCheck, UilTimes, UilFileDownload, UilTimesCircle, UilFileAlt } from '@iconscout/react-unicons';
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';

// Internal module imports
import Typography from '../../../components/Typography/Typography';
import { useLoader } from '../../../hooks';
import Table from '../../../components/Table/Table';
import { momentTimeFormater } from '../../../utils/timeHelper';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import ModalComponent from '../../../components/ModalComponent/ModalComponent';
import { TextArea } from '../../../components/Inputs/Input';
import Dropdown from '../../../components/Inputs/Dropdown';
import { incidentValidation } from '../../../validations/Edge/incidentValidation';
import { IncidentsService } from '../../../services/EdgeIncidentService';
import { Capitalize } from '../../../utils/stringHelper';
import DeleteModal from '../../../components/DeleteModal/DeleteModal';
import incidentClasses from './EdgeIncidents.module.css';
import { DownloadAsExcel } from '../../../utils/downloadAsExcel';
import Checkbox from '../../../components/Inputs/Checkbox/Checkbox';
import MultipleFilesUpload from '../../../components/Inputs/FileUpload/MultipleFileUpload';

// CSS Imports
import classes from '../EdgeError/EdgeError.module.css';
import bidClasses from '../../../styles/Bids.module.css';
import bidsClasses from '../../../styles/BidModal.module.css';
import IncidentFileUploadHistory from '../ModalComponent/IncidentFileUploadHistory';

const typeOptions = [
    { label: 'External', value: 'external' },
    { label: 'Internal', value: 'internal' },
    { label: 'Deployment', value: 'deployment' },
];

const statusOptions = [
    { label: 'Open', value: 'opened' },
    { label: 'Aknowledge', value: 'acknowledged' },
    { label: 'Close', value: 'closed' },
];

const priorityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
];

const deploymentCheckList = [
    {
        "question": "Is the latest docker image from tg-edge-main onto the platform?",
        "status": false
    },
    {
        "question": "Is there any software/application running in the background on the system where you’re going to deploy?",
        "status": false
    },
    {
        "question": "Is every important information (such as meter Type/IP, etc.) relevant to the instance in the documents section of the instance attached?",
        "status": false
    },
    {
        "question": "Is the necessary profiles for the meter and the control respectively attached?",
        "status": false
    },
    {
        "question": "Is changes to the controller profile for any specific edge case are made?",
        "status": false
    },
    {
        "question": "Is the capacityFactor set to default or leave it as 1 according to the License Type and the capacity?",
        "status": false
    },
    {
        "question": "Is the monitoring toggle and profile toggle in the edge profile and customer profile enabled?",
        "status": false
    },
    {
        "question": "Create a new Incident in TG when there is a Deployment.",
        "status": false
    },
    {
        "question": "Check if the edge is reporting to the respective signal.",
        "status": false
    },
    {
        "question": "Check the Boot Notification and Code version for that particular Edge.",
        "status": false
    }
];

const LIMIT = 15;

const EdgeIncidents = ({ deviceId, setOpen }) => {
    const [startLoader, stopLoader] = useLoader();
    const [totalCount, setTotalCount] = useState(0);

    const [tableData, setTableData] = useState([]);
    const [createIncidentModal, setCreateIncidentModal] = useState({ status: false, data: {} });
    const [deleteModal, setDeleteModal] = useState({ status: false, id: '' });

    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState(0);
    const [totalPageCount, setTotalPageCount] = useState(0);

    const [checkList, setCheckList] = useState(deploymentCheckList);

    const [files, setFiles] = useState([]);
    const [incidentFileListModal, setIncidentFileListModal] = useState({ status: false, data: {} });

    useEffect(() => {
        getEdgeIncidents();
    }, [deviceId, skip]);

    const getEdgeIncidents = () => {
        if (deviceId) {
            IncidentsService.GetIncidents(deviceId, { skip, limit: LIMIT }, startLoader, handleEdgeIncidentsSuccess, handleError, stopLoader);
        }
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    const handleDownload = () => {
        let data = tableData.map((i) => {
            const item = i.item;
            return {
                status: item.status ? Capitalize(item.status) : '--',
                priority: item.priority,
                type: item.type ? Capitalize(item.type) : '--',
                notes: item.notes,
                marketStatus: item.marketStatus,
                openedAt: item.createdAt ? momentTimeFormater(item.createdAt).format('YYYY-MM-DD HH:mm') : '--',
                closedAt: item.closedAt ? momentTimeFormater(item.closedAt).format('YYYY-MM-DD HH:mm') : '--',
            }
        });
        DownloadAsExcel(data, `Edge Incident Logs`, ['Status', 'Priority', 'Type', 'Notes', 'Market Status', 'Opened At (CET)', 'Closed At (CET)']);
    }

    const handleEdgeIncidentsSuccess = ({ data }) => {
        const items = data.data?.data || [];
        const count = data.data?.count || 0;
        const processedData = items.map((item, index) => {
            const notes = item.notes;
            return {
                item,
                slNo: index + 1 + skip,
                status: item.status ? Capitalize(item.status) : '--',
                priority: (
                    <div className={classes.DataMonitoringContainer}>
                        {item.priority === 'low' ? (
                            <div className={classes.Low}></div>
                        ) : item.priority === 'high' ? (
                            <div className={classes.High}></div>
                        ) : (
                            <div className={classes.Medium}></div>
                        )}
                    </div>
                ),
                type: item.type ? Capitalize(item.type) : '--',
                notes: notes.length > 35 ? <CustomTooltip content={notes}>{notes.substring(0, 35) + '...'}</CustomTooltip> : notes || '--',
                marketStatus: item.marketStatus ? <UilCheck size="1.5vw" style={{ color: 'green' }} /> : <UilTimes size="1.5vw" style={{ color: 'red' }} />,
                openedAt: item.createdAt ? momentTimeFormater(item.createdAt).format('YYYY-MM-DD HH:mm') : '--',
                closedAt: item.closedAt ? momentTimeFormater(item.closedAt).format('YYYY-MM-DD HH:mm') : '--',
                action: (
                    <div
                        style={{
                            display: 'flex',
                            gridColumnGap: '1vw',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <CustomTooltip content={'Edit'}>
                            <UilPen size={'1.2vw'} style={{ color: 'var(--color-primary)' }} onClick={() => {
                                setCreateIncidentModal({ status: true, data: item });
                                setFiles(Array.isArray(item.files) ? item.files : []);
                            }} />
                        </CustomTooltip>
                        <CustomTooltip content={'View files'}>
                            <UilFileAlt size={'1.2vw'} style={{ color: 'var(--color-primary)' }} onClick={() => setIncidentFileListModal({ status: true, data: item })} />
                        </CustomTooltip>
                        <CustomTooltip content={'Delete'}>
                            <UilTrash size={'1.2vw'} style={{ color: 'var(--color-primary)' }} onClick={() => setDeleteModal({ status: true, id: item._id })} />
                        </CustomTooltip>
                    </div>
                ),
            };
        });
        setTableData(processedData);
        setTotalCount(count);
        setTotalPageCount(count);
    };

    const handleSubmit = (values) => {
        values.files = files;

        if (createIncidentModal.data?._id) {
            if (createIncidentModal.data?.closedAt) {
                values.closedAt = createIncidentModal.data?.closedAt;
            }
            IncidentsService.Update(createIncidentModal.data?._id, values, startLoader, () => handleSuccess('update'), handleError, stopLoader);
        } else {
            if (deviceId) {
                if (values.type === 'deployment' && !checkList.every((item) => item.status === true)) {
                    toast.error('All check list should be checked to create incident.');
                    return;
                }
                IncidentsService.Create(
                    deviceId,
                    values,
                    startLoader,
                    () => {
                        handleSuccess('create');
                        setCheckList(deploymentCheckList);
                    },
                    handleError,
                    stopLoader
                );
            }
        }
    };

    const handleSuccess = (type) => {
        getEdgeIncidents();
        setCreateIncidentModal({ status: false, data: {} });
        setFiles([]);
        toast.success(`Incident ${type} successfully`);
    };

    const handleDelete = () => {
        IncidentsService.Delete(deleteModal.id, startLoader, handleDeleteSuccess, handleError, stopLoader);
    };

    const handleDeleteSuccess = (res) => {
        getEdgeIncidents();
        setDeleteModal({ status: false, id: '' });
        toast.success('Incident deleted successfully');
    };

    const handlePageChange = (pageno) => {
        setSkip(LIMIT * pageno);
        setPage(pageno);
    };

    const handleChecked = (idx) => {
        setCheckList((prev) =>
            prev.map((item, index) =>
                index === idx ? { ...item, status: !item.status } : item
            )
        );
    }

    const handleFilesSelect = (e) => {
        const files = e.target.files;
        uploadFiles(files);
    };

    const uploadFiles = (files) => {
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }
        
        IncidentsService.UploadFiles(deviceId, formData, startLoader, handleUploadFilesSuccess, handleError, stopLoader);
    };

    const handleUploadFilesSuccess = ({ data }) => {
        setFiles((prev) => [...prev, ...data.data]);
    };

    const removeFile = (key, id) => {
        IncidentsService.RemoveIncidentUploadFile(id, startLoader, handleRemoveFileSuccess, handleError, stopLoader);
        const temp = files.filter((e) => e.key !== key);
        setFiles(temp);
    };

    const handleRemoveFileSuccess = () => {
        toast.success('File removed successfully');
    };

    return (
        <div className={bidClasses.Bids}>
            <div className={classes.Header}>
                <div className={classes.ErrorDropdownContainer}>
                    <Typography content={'Edge Incidents'} />
                    <div className={bidClasses.TableCount}>
                        Total Count :
                        <span>
                            <Typography size="14" content={totalCount || 0} />
                        </span>
                    </div>
                </div>
                <div className={classes.ErrorDropdownContainer}>
                    <button
                        onClick={() => {
                            setCreateIncidentModal({ status: true, data: '' });
                            setFiles([]);
                        }}
                        className="btn-primary"
                        style={{ marginTop: '0' }}
                    >
                        Create Incidents
                    </button>
                    <CustomTooltip content={'Download'}>
                        <div className={classes.Action} onClick={() => handleDownload()}>
                            <div>
                                <UilImport size={'1.2vw'} style={{ color: 'var(--color-primary)', cursor: 'pointer' }} />
                            </div>
                        </div>
                    </CustomTooltip>
                </div>
            </div>

            <div>
                <Table
                    head={['Sl.No', 'STATUS', 'PRIORITY', 'TYPE', 'NOTES', 'MARKET STATUS', 'OPENED AT (CET)', 'CLOSED AT (CET)', 'ACTION']}
                    keys={['slNo', 'status', 'priority', 'type', 'notes', 'marketStatus', 'openedAt', 'closedAt', 'action']}
                    data={tableData}
                    page={page}
                    Pagination={true}
                    limit={LIMIT}
                    handlePageChange={handlePageChange}
                    totalCount={totalPageCount}
                />
            </div>

            <ModalComponent isOpen={createIncidentModal.status} setOpen={() => {
                setCreateIncidentModal({ status: false, data: '' });
                setFiles([]);
            }}>
                <div style={{ minWidth: '50vw' }}>
                    <Typography content={createIncidentModal.data?._id ? 'Update Incident' : 'Create Incident'} />

                    <Formik
                        initialValues={{
                            status: createIncidentModal.data?.status ? createIncidentModal.data?.status : statusOptions[0]?.value,
                            priority: createIncidentModal.data?.priority ? createIncidentModal.data?.priority : priorityOptions[0]?.value,
                            type: createIncidentModal.data?.type ? createIncidentModal.data?.type : typeOptions[0]?.value,
                            notes: createIncidentModal.data?.notes ? createIncidentModal.data?.notes : '',
                            files: createIncidentModal.data?.files ? createIncidentModal.data?.files : [],
                        }}
                        enableReinitialize
                        validationSchema={incidentValidation}
                        onSubmit={(val) => {
                            handleSubmit(val);
                        }}
                    >
                        {({ errors, touched, values, isValidating, ...props }) => (
                            <Form>
                                <div>
                                    <div>
                                        <div className={bidsClasses.InputContainer}>
                                            <div className={bidsClasses.FieldControl2}>
                                                <label for="status">
                                                    Status <span className="required">*</span>
                                                </label>
                                                <Dropdown
                                                    name="status"
                                                    options={statusOptions}
                                                    defaultValue={createIncidentModal.data?.status ? { value: createIncidentModal.data?.status } : statusOptions[0]}
                                                />
                                            </div>
                                            <div className={bidsClasses.FieldControl2}>
                                                <label for="priority">
                                                    Priority <span className="required">*</span>
                                                </label>
                                                <Dropdown
                                                    name="priority"
                                                    options={priorityOptions}
                                                    defaultValue={createIncidentModal.data?.priority ? { value: createIncidentModal.data?.priority } : priorityOptions[0]}
                                                />
                                            </div>
                                        </div>
                                        <div className={bidsClasses.InputContainer}>
                                            <div className={bidsClasses.FieldControl2}>
                                                <label for="type">
                                                    Type <span className="required">*</span>
                                                </label>
                                                <Dropdown
                                                    name="type"
                                                    options={typeOptions}
                                                    defaultValue={createIncidentModal.data?.type ? { value: createIncidentModal.data?.type } : typeOptions[0]}
                                                />
                                            </div>
                                        </div>
                                        <div className={bidsClasses.InputContainer}>
                                            <div className={bidsClasses.FieldControl2}>
                                                <label for="notes">
                                                    Notes <span className="required">*</span>
                                                </label>
                                                <TextArea name={`notes`} id="notes" type="text" />
                                            </div>
                                        </div>

                                        {values?.type === 'deployment' && (
                                            <div>
                                                <div className={incidentClasses.FormHeading}>
                                                    Check List <span className="required">*</span>
                                                </div>
                                                <div className={incidentClasses.FormContainer}>
                                                    {checkList.map((item, idx) => {
                                                        return (
                                                            <div className={incidentClasses.CheckList}>
                                                                <Checkbox onChange={() => handleChecked(idx)} checked={item.status} />
                                                                <div className={incidentClasses.CheckListQuestion}>{item.question}</div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <div className={bidsClasses.InputContainer}>
                                            <div className={bidsClasses.FieldControl2} style={{ width: '99.7%' }}>
                                                <label for="uploadFiles">Files</label>
                                                <div>
                                                    <MultipleFilesUpload name="uploadFiles" id="uploadFiles" accept="*" multiple onChange={handleFilesSelect} />
                                                </div>
                                            </div>
                                        </div>

                                        {files?.length ? (
                                            <div className={incidentClasses.UploadedEdgeFiles}>
                                                {files.map((item, index) => (
                                                    <div>
                                                        <div className={incidentClasses.SelectedFile}>
                                                            <div key={index}>
                                                                <p>{item.name}</p>
                                                            </div>
                                                            <div style={{ display: 'flex' }}>
                                                                <div className={incidentClasses.CloseIcon}>
                                                                    <UilFileDownload
                                                                        style={{
                                                                            color: 'var(--color-primary)',
                                                                        }}
                                                                        size={'1.3vw'}
                                                                    />
                                                                </div>
                                                                <div onClick={() => removeFile(item.key, item._id)} className={incidentClasses.CloseIcon}>
                                                                    <UilTimesCircle
                                                                        style={{
                                                                            color: 'var(--color-primary)',
                                                                        }}
                                                                        size={'1.3vw'}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            ''
                                        )}

                                        <div className={bidsClasses.ButtonContainer}>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    onClick={(e) => {
                                                        setCreateIncidentModal({ status: false, data: '' });
                                                        setFiles([]);
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                            <div>
                                                <button className="btn-primary" type="submit">
                                                    Submit
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </ModalComponent>

            <ModalComponent isOpen={incidentFileListModal.status} setOpen={() => setIncidentFileListModal({ status: false, data: '' })}>
                <div style={{ minWidth: '50vw' }}>
                    <IncidentFileUploadHistory incidentId={incidentFileListModal.data?._id} getEdgeIncidents={getEdgeIncidents} />
                </div>
            </ModalComponent>

            <DeleteModal
                deletefunction={handleDelete}
                opendeleteModal={deleteModal.status}
                setOpenDeleteModal={(status) => setDeleteModal({ ...deleteModal, status })}
            ></DeleteModal>
        </div>
    );
};

export default EdgeIncidents;
